import { useInfiniteQuery } from 'react-query';
import type { UseInfiniteQueryOptions } from 'react-query';
import {
  getChatRoomsList,
  type GetChatRoomsListResponse,
  type GetChatRoomsQuery
} from '../../services/chatService';

const DEFAULT_PER_PAGE = 15;

const CATEGORY_MAP: Record<string, NonNullable<GetChatRoomsQuery>['categories']> = {
  DM: ['dm'],
  Trading: ['selling', 'purchasing'],
  Exchanging: ['exchanging']
};

export type ChatRoomsCategory = 'All' | 'DM' | 'Trading' | 'Exchanging';

export interface UseGetChatRoomsParams {
  type?: ChatRoomsCategory;
  tradingPhotoCardId?: string;
  onlyJoined?: boolean;
  onlyMine?: boolean;
  perPage?: number;
  categories?: NonNullable<GetChatRoomsQuery>['categories'];
  categoryFilterType?: NonNullable<GetChatRoomsQuery>['category_filter_type'];
}

export interface UseGetChatRoomsOptions
  extends UseInfiniteQueryOptions<GetChatRoomsListResponse, Error> {}

export const chatRoomsQueryKeyPrefix = ['chatRooms'] as const;

export function useGetChatRooms(
  {
    type = 'All',
    tradingPhotoCardId,
    onlyJoined = false,
    onlyMine = false,
    perPage = DEFAULT_PER_PAGE,
    categories: categoriesOverride,
    categoryFilterType
  }: UseGetChatRoomsParams,
  options?: UseGetChatRoomsOptions
) {
  const categories = categoriesOverride ?? CATEGORY_MAP[type];

  const query: GetChatRoomsQuery = {
    only_joined: onlyJoined || undefined,
    only_mine: onlyMine || undefined,
    trading_photo_card_id: tradingPhotoCardId,
    categories,
    category_filter_type: categoryFilterType,
    per_page: perPage
  };

  const enabled =
    tradingPhotoCardId !== undefined
      ? !!tradingPhotoCardId
      : (options?.enabled ?? true);

  return useInfiniteQuery<GetChatRoomsListResponse, Error>(
    [...chatRoomsQueryKeyPrefix, query],
    ({ pageParam = 1 }) => getChatRoomsList({ query: { ...query, page: pageParam } }),
    {
      enabled,
      getNextPageParam: (lastPage) => {
        const { currentPage, totalPages } = lastPage?.meta?.pagination ?? {};
        if (typeof currentPage === 'number' && typeof totalPages === 'number') {
          return currentPage < totalPages ? currentPage + 1 : undefined;
        }
        return undefined;
      },
      staleTime: 1000 * 5,
      cacheTime: 1000 * 10,
      ...options
    }
  );
}
